import React, { useState, useEffect, useMemo } from "react";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import {
  XMarkIcon,
  CalendarDaysIcon,
  ClockIcon,
  UserIcon,
  ChatBubbleLeftEllipsisIcon,
  PhoneIcon,
  VideoCameraIcon,
  EnvelopeIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { Fragment } from "react";
import { Button, Card } from "components/ui";
import { Input, Textarea, Select } from "components/ui/Form";
import { formatDate, formatTime, getLocalTime, getLocalISODate } from "utils/dateUtils";
import { getUsersForAssignment } from "services/followUpService";
import { getAllLeads } from "services/queryService";
import { updateLeadOwner } from "services/leadsService";
import { ConfirmModal } from "components/shared/ConfirmModal";
import { toast } from "sonner";

const FollowUpScheduleModal = ({
  isOpen,
  onClose,
  leadData,
  onScheduleFollowUp,
  enableLeadSelection = false,
  selectedDate: preSelectedDate = null,
  editingFollowUp = null
}) => {
  const isEditMode = editingFollowUp && !editingFollowUp.isReschedule;
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState("09:00");
  const [followUpType, setFollowUpType] = useState("call");
  const [notes, setNotes] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [currentViewDate, setCurrentViewDate] = useState(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedLead, setSelectedLead] = useState("");
  const [availableLeads, setAvailableLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignState, setReassignState] = useState("pending");
  const [pendingAssignedTo, setPendingAssignedTo] = useState("");
  const [reassignLoading, setReassignLoading] = useState(false);

  // Load users for assignment when modal opens
  useEffect(() => {
    if (isOpen) {
      loadUsers();
      if (enableLeadSelection) {
        loadLeads();
      }
    }
  }, [isOpen, enableLeadSelection]);

  // Reset form when modal opens or fill with editing data
  useEffect(() => {
    if (isOpen) {
      if (editingFollowUp) {
        // Pre-fill form with editing data
        // Handle Date - check all possible variations
        const editDateRaw =
          editingFollowUp.ScheduledDate ||
          editingFollowUp.scheduledDate ||
          editingFollowUp.date ||
          editingFollowUp.Date;
        const editDate = editDateRaw ? new Date(editDateRaw) : new Date();
        setSelectedDate(editDate);

        // Handle Time - check all possible variations
        // Prefer already localized 'scheduledTime' if available
        let timeValue =
          editingFollowUp.scheduledTime ||
          editingFollowUp.time ||
          editingFollowUp.Time ||
          editingFollowUp.ScheduledTime ||
          "09:00";

        // Logic to detect if it's raw UTC (usually HH:mm:ss from API) and localize it
        // The service usually provides localized strings with AM/PM (e.g. "9:00 AM")
        // If it's a raw HH:mm or HH:mm:ss string without AM/PM, it's likely UTC from the API
        if (timeValue &&
          typeof timeValue === 'string' &&
          !timeValue.match(/(AM|PM)/i) &&
          timeValue.includes(':')) {
          const isoDate = getLocalISODate(editDate);
          const localized = getLocalTime(isoDate, timeValue);
          if (localized) timeValue = localized;
        }

        // Helper: convert "3:30 PM" / "03:30 PM" 12h format → "15:30" 24h HH:mm
        const convertTo24h = (t) => {
          if (!t || typeof t !== 'string') return t;
          const match = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
          if (match) {
            let h = parseInt(match[1], 10);
            const m = match[2];
            const period = match[3].toUpperCase();
            if (period === 'PM' && h !== 12) h += 12;
            if (period === 'AM' && h === 12) h = 0;
            return `${String(h).padStart(2, '0')}:${m}`;
          }
          // Already HH:mm or HH:mm:ss — strip seconds and normalize
          if (t.includes(':')) {
            const parts = t.split(':');
            return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
          }
          return t;
        };

        timeValue = convertTo24h(timeValue);
        setSelectedTime(timeValue);

        // Handle Follow-up Type / Channel
        const channelId = editingFollowUp.channelId || editingFollowUp.ChannelId;
        const channelToType = {
          1: "call",
          2: "email",
          3: "meeting",
          4: "whatsapp",
          5: "sms"
        };
        // channelNameToType handles cases where the API sends a string name like "Call"
        const channelNameToType = (name) => {
          if (!name || typeof name !== 'string') return null;
          const n = name.toLowerCase();
          if (n.includes('call') || n.includes('phone')) return 'call';
          if (n.includes('email')) return 'email';
          if (n.includes('meeting')) return 'meeting';
          if (n.includes('whatsapp')) return 'whatsapp';
          if (n.includes('sms')) return 'sms';
          return null;
        };

        const rawType = editingFollowUp.type || editingFollowUp.Type || editingFollowUp.ChannelName || editingFollowUp.channelName || '';
        const type =
          channelNameToType(rawType) ||
          channelToType[channelId] ||
          'call';
        setFollowUpType(type);

        // Handle Notes/Remarks
        // For edit, use Notes as that's what comes from the backend API
        const notesValue =
          editingFollowUp.Notes ||
          editingFollowUp.notes ||
          editingFollowUp.Remarks ||
          editingFollowUp.remarks ||
          "";
        setNotes(notesValue);

        // Handle User Assignment
        const assignedToValue =
          editingFollowUp.AssignedToId ||
          editingFollowUp.assignedToId ||
          editingFollowUp.assignedTo ||
          editingFollowUp.AssignedTo ||
          "";
        setAssignedTo(assignedToValue);

        // Handle Lead Selection
        const leadIdValue =
          editingFollowUp.LeadId ||
          editingFollowUp.leadId ||
          editingFollowUp.id ||
          (leadData?.id || leadData?.leadId || "");
        setSelectedLead(leadIdValue);

        // Set view date to show the month of the follow-up
        setCurrentViewDate(editDate);
      } else {
        // Reset form for new follow-up
        const dateToSet = preSelectedDate ? new Date(preSelectedDate) : new Date();
        setSelectedDate(dateToSet);
        setSelectedTime("09:00");
        setFollowUpType("call");
        setNotes("");
        setAssignedTo(leadData?.assignedTo || "");
        setSelectedLead(enableLeadSelection ? "" : (leadData?.id || ""));
        setCurrentViewDate(dateToSet);
      }
    }
  }, [isOpen, preSelectedDate, leadData, enableLeadSelection, editingFollowUp]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const result = await getUsersForAssignment();
      if (result.success) {
        setAvailableUsers(result.data);
        // Set current user as default only if not editing; use functional update
        // so we don't rely on a potentially stale `assignedTo` value.
        if (result.data.length > 0 && !editingFollowUp) {
          // Only use functional update to preserve existing value if set (e.g. from lead owner patch)
          setAssignedTo((prev) => prev || "");
        }
      }
    } catch (error) {
      console.error("Error loading users:", error);
      toast.error("Failed to load users", {
        description: "Could not fetch available users for assignment",
        duration: 3000,
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadLeads = async () => {
    setLoadingLeads(true);
    try {
      const result = await getAllLeads();
      if (result.success) {
        // Transform leads data for dropdown based on API structure
        const leadsOptions = result.data.map(lead => {
          const email = lead.Email || lead.email;
          const phone = lead.Phone || lead.phone;
          const contactInfo = email || phone || 'No contact info';

          return {
            value: lead.Id || lead.id || lead.leadId,
            label: `${lead.LeadName || lead.name} - ${contactInfo}`,
            name: lead.LeadName || lead.name,
            email: email,
            phone: phone,
            ownerName: lead.OwnerName || lead.assignedToName || lead.AssignedToName,
            ownerId: lead.OwnerId || lead.assignedTo || lead.AssignedTo
          };
        });
        setAvailableLeads(leadsOptions);
      }
    } catch (error) {
      console.error("Error loading leads:", error);
      toast.error("Failed to load leads", {
        description: "Could not fetch available leads",
        duration: 3000,
      });
    } finally {
      setLoadingLeads(false);
    }
  };

  // Auto-patch owner when lead is selected
  useEffect(() => {
    if (enableLeadSelection && selectedLead && availableLeads.length > 0 && availableUsers.length > 0) {
      const selectedLeadData = availableLeads.find(l => l.value === selectedLead);
      if (selectedLeadData) {
        // Try to match by ID first, then by name
        if (selectedLeadData.ownerId) {
          const userById = availableUsers.find(u => u.value === selectedLeadData.ownerId);
          if (userById) {
            setAssignedTo(userById.value);
            return;
          }
        }

        if (selectedLeadData.ownerName && selectedLeadData.ownerName !== "N/A") {
          const userByName = availableUsers.find(u =>
            u.label === selectedLeadData.ownerName ||
            u.name === selectedLeadData.ownerName ||
            u.label.includes(selectedLeadData.ownerName)
          );
          if (userByName) {
            setAssignedTo(userByName.value);
          }
        }
      }
    }
  }, [selectedLead, availableLeads, availableUsers, enableLeadSelection]);

  // Follow-up type options
  const followUpTypes = [
    { value: "call", label: "Phone Call", icon: PhoneIcon, color: "primary" },
    { value: "email", label: "Email", icon: EnvelopeIcon, color: "success" },
    { value: "meeting", label: "Meeting", icon: VideoCameraIcon, color: "secondary" },
    { value: "whatsapp", label: "WhatsApp", icon: ChatBubbleLeftEllipsisIcon, color: "info" },
  ];


  // Team members (mock data - replace with actual team members)
  // teamMembers is now replaced by availableUsers loaded from API

  // Time slots
  const timeSlots = [
    "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
    "12:00", "12:30", "13:00", "13:30", "14:00", "14:30",
    "15:00", "15:30", "16:00", "16:30", "17:00", "17:30"
  ];

  // Calendar logic
  const { calendarDays, monthYear } = useMemo(() => {
    const year = currentViewDate.getFullYear();
    const month = currentViewDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const currentDay = new Date(startDate);

    // Generate 6 weeks (42 days) to ensure full calendar grid
    for (let i = 0; i < 42; i++) {
      const isCurrentMonth = currentDay.getMonth() === month;
      const isToday = currentDay.toDateString() === new Date().toDateString();
      const isPast = currentDay < new Date().setHours(0, 0, 0, 0);
      const isSelected = currentDay.toDateString() === selectedDate.toDateString();

      days.push({
        date: new Date(currentDay),
        day: currentDay.getDate(),
        isCurrentMonth,
        isToday,
        isPast,
        isSelected
      });

      currentDay.setDate(currentDay.getDate() + 1);
    }

    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];

    return {
      calendarDays: days,
      monthYear: `${monthNames[month]} ${year}`
    };
  }, [currentViewDate, selectedDate]);

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const navigateMonth = (direction) => {
    setCurrentViewDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const handleDateSelect = (date) => {
    if (date >= new Date().setHours(0, 0, 0, 0)) { // Only allow future dates
      setSelectedDate(date);
    }
  };

  // Utility function to check if selected date and time is in the past
  const isScheduleInPast = (date, time) => {
    const now = new Date();
    const scheduleDateTime = new Date(date);
    const [hours, minutes] = time.split(':').map(Number);
    scheduleDateTime.setHours(hours, minutes, 0, 0);

    return scheduleDateTime <= now;
  };

  // Auto-adjust time when date changes to ensure valid selection
  // Skip auto-adjustment in edit mode (non-reschedule) to preserve the original scheduled time
  useEffect(() => {
    if (isEditMode) return; // Don't auto-adjust time when editing an existing follow-up

    if (selectedDate && selectedTime) {
      // If the currently selected time is in the past for the selected date
      if (isScheduleInPast(selectedDate, selectedTime)) {
        // Find the next available time slot
        const nextValidTime = timeSlots.find(time => !isScheduleInPast(selectedDate, time));
        if (nextValidTime) {
          setSelectedTime(nextValidTime);
        } else {
          // If no valid time slots for today, set to 09:00 (user will need to pick a future date)
          setSelectedTime("09:00");
        }
      }
    }
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    const leadId = enableLeadSelection ? selectedLead : (leadData?.id || editingFollowUp?.leadId);

    // Validate required fields
    if (!selectedDate) {
      toast.error("Please select a date", {
        description: "A follow-up date is required",
      });
      return;
    }

    if (!selectedTime) {
      toast.error("Please select a time", {
        description: "A follow-up time is required",
      });
      return;
    }

    // Validate that the scheduled date and time is not in the past
    if (isScheduleInPast(selectedDate, selectedTime)) {
      toast.error("Cannot schedule follow-up in the past", {
        description: "Please select a future date and time for the follow-up",
      });
      return;
    }

    if (!followUpType) {
      toast.error("Please select follow-up type", {
        description: "Choose the type of follow-up (call, email, etc.)",
      });
      return;
    }

    if (!assignedTo) {
      toast.error("Please assign the follow-up", {
        description: "Select who should handle this follow-up",
      });
      return;
    }

    if (enableLeadSelection && !selectedLead) {
      toast.error("Please select a lead", {
        description: "Choose which lead this follow-up is for",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const followUpData = {
        leadId: leadId,
        date: selectedDate,
        time: selectedTime,
        type: followUpType,
        // In edit mode, preserve the original channelId so the backend doesn't change the channel
        channelId: editingFollowUp
          ? (editingFollowUp.channelId || editingFollowUp.ChannelId)
          : undefined,
        notes,
        assignedTo,
      };


      if (onScheduleFollowUp) {
        const success = await onScheduleFollowUp(followUpData);
        if (success) {
          onClose();
        }
      } else {
        onClose();
      }
    } catch (error) {
      console.error("Error scheduling follow-up:", error);
      toast.error("Failed to schedule follow-up", {
        description: error.message || "An unexpected error occurred",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTypeIcon = (type) => {
    const typeData = followUpTypes.find(t => t.value === type);
    return typeData ? typeData.icon : PhoneIcon;
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        onClose={() => { }}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="relative flex w-full max-w-4xl origin-top flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 dark:bg-dark-700 outline-none focus:outline-none">
            {/* Header */}
            <div className="flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 dark:bg-dark-800 sm:px-5">
              <div className="flex items-center gap-3">
                <CalendarDaysIcon className="h-5 w-5 text-gray-600 dark:text-dark-300" />
                <div>
                  <DialogTitle as="h3" className="text-base font-medium text-gray-800 dark:text-dark-100">
                    {editingFollowUp
                      ? (editingFollowUp.isReschedule ? "Reschedule Follow-up" : "Edit Follow-up")
                      : enableLeadSelection
                        ? "Create Follow-up"
                        : "Schedule Follow-up"
                    }
                  </DialogTitle>
                  <p className="text-sm text-gray-600 dark:text-dark-300">
                    {editingFollowUp
                      ? `${editingFollowUp.isReschedule ? 'Rescheduling' : 'Editing'} follow-up for ${editingFollowUp.userName || editingFollowUp.LeadName || leadData?.name || "Lead"}`
                      : enableLeadSelection
                        ? "Select a lead and schedule follow-up"
                        : `for ${leadData?.name || "Lead"}`
                    }
                  </p>
                </div>
              </div>
              <Button
                onClick={onClose}
                variant="flat"
                isIcon
                className="size-7 rounded-full ltr:-mr-1.5 rtl:-ml-1.5"
              >
                <XMarkIcon className="size-4.5" />
              </Button>
            </div>

            <div className="flex flex-col overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-200 dark:divide-dark-600">
                {/* Left Side - Calendar */}
                <div className="p-4 sm:p-6">
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-dark-100 mb-3">
                      Select Date
                    </h4>

                    {/* Calendar Header */}
                    <div className={`flex items-center justify-between mb-4 ${isEditMode ? 'opacity-40 pointer-events-none grayscale' : ''}`}>
                      <Button
                        onClick={() => navigateMonth(-1)}
                        variant="flat"
                        isIcon
                        className="h-8 w-8"
                        disabled={editingFollowUp && !editingFollowUp.isReschedule}
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </Button>
                      <h5 className="text-lg font-semibold text-gray-900 dark:text-dark-100">
                        {monthYear}
                      </h5>
                      <Button
                        onClick={() => navigateMonth(1)}
                        variant="flat"
                        isIcon
                        className="h-8 w-8"
                        disabled={editingFollowUp && !editingFollowUp.isReschedule}
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Calendar Grid */}
                    <div className={`grid grid-cols-7 gap-1 ${isEditMode ? 'opacity-40 pointer-events-none grayscale' : ''}`}>
                      {/* Day Headers */}
                      {dayNames.map(day => (
                        <div key={day} className="p-2 text-center text-xs font-medium text-gray-500 dark:text-dark-400">
                          {day}
                        </div>
                      ))}

                      {/* Calendar Days */}
                      {calendarDays.map((day, index) => (
                        <button
                          key={index}
                          onClick={() => handleDateSelect(day.date)}
                          disabled={day.isPast || (editingFollowUp && !editingFollowUp.isReschedule)}
                          className={`
                            p-2 text-sm rounded-lg transition-colors relative
                            ${day.isCurrentMonth
                              ? day.isPast
                                ? "text-gray-300 dark:text-dark-600 cursor-not-allowed"
                                : day.isSelected
                                  ? "bg-primary-600 text-white font-semibold"
                                  : day.isToday
                                    ? "bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400 font-semibold"
                                    : "text-gray-900 dark:text-dark-100 hover:bg-gray-100 dark:hover:bg-dark-600"
                              : "text-gray-400 dark:text-dark-500"
                            }
                          `}
                        >
                          {day.day}
                          {day.isToday && !day.isSelected && (
                            <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-primary-600 dark:bg-primary-400 rounded-full" />
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Selected Date Display */}
                    {selectedDate && (
                      <Card className="bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800 p-3">
                        <div className="flex items-center gap-2 text-primary-700 dark:text-primary-300">
                          <CalendarDaysIcon className="h-4 w-4" />
                          <span className="text-sm font-medium">
                            Selected: {formatDate(selectedDate)}
                          </span>
                        </div>
                      </Card>
                    )}
                  </div>
                </div>

                {/* Right Side - Follow-up Details */}
                <div className="p-4 sm:p-6 space-y-6">
                  {/* Follow-up Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-3">
                      Follow-up Type
                      {isEditMode && (
                        <span className="ml-2 text-xs font-normal text-gray-400 dark:text-gray-500"></span>
                      )}
                    </label>
                    <div className={`grid grid-cols-2 gap-2 ${isEditMode ? 'opacity-60 pointer-events-none' : ''}`}>
                      {followUpTypes.map((type) => {
                        const Icon = type.icon;
                        return (
                          <button
                            key={type.value}
                            onClick={() => !isEditMode && setFollowUpType(type.value)}
                            disabled={isEditMode}
                            title={isEditMode ? 'Follow-up type cannot be changed after creation' : undefined}
                            className={`
                            p-3 rounded-lg border-2 transition-all flex items-center gap-2 text-sm
                            ${followUpType === type.value
                                ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300"
                                : "border-gray-200 dark:border-dark-600 hover:border-gray-300 dark:hover:border-dark-500 text-gray-700 dark:text-dark-300"
                              }
                            ${isEditMode ? 'cursor-not-allowed' : ''}
                          `}
                          >
                            <Icon className="h-4 w-4" />
                            {type.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Lead Selection (only when enableLeadSelection is true) */}
                  {enableLeadSelection && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-2">
                        Select Lead *
                      </label>
                      <Select
                        value={selectedLead}
                        onChange={(e) => setSelectedLead(e.target.value)}
                        className="w-full"
                        disabled={loadingLeads}
                      >
                        {loadingLeads ? (
                          <option value="">Loading leads...</option>
                        ) : availableLeads.length > 0 ? (
                          <>
                            <option value="">Choose a lead...</option>
                            {availableLeads.map(lead => (
                              <option key={lead.value} value={lead.value}>
                                {lead.label}
                              </option>
                            ))}
                          </>
                        ) : (
                          <option value="">No leads available</option>
                        )}
                      </Select>
                      {loadingLeads && (
                        <div className="mt-1 text-xs text-gray-500 dark:text-dark-400 flex items-center gap-1">
                          <div className="animate-spin h-3 w-3 border border-gray-300 border-t-transparent rounded-full" />
                          Loading available leads...
                        </div>
                      )}
                    </div>
                  )}

                  {/* Time Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-2">
                      Time
                    </label>
                    <Select
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                      className="w-full"
                      disabled={editingFollowUp && !editingFollowUp.isReschedule}
                    >
                      {timeSlots.map(time => {
                        // Check if this time slot is in the past for the selected date
                        const isTimeDisabled = isScheduleInPast(selectedDate, time);
                        const isToday = selectedDate.toDateString() === new Date().toDateString();
                        // In edit mode, don't mark options as disabled so the browser
                        // can still display and select past time values correctly.
                        const shouldDisable = isEditMode ? false : isTimeDisabled;

                        return (
                          <option
                            key={time}
                            value={time}
                            disabled={shouldDisable}
                            style={{
                              color: isTimeDisabled && !isEditMode ? '#9ca3af' : 'inherit',
                              fontStyle: isTimeDisabled && !isEditMode ? 'italic' : 'normal'
                            }}
                          >
                            {(parseInt(time.split(':')[0]) % 12 || 12) + ':' + time.split(':')[1] + (parseInt(time.split(':')[0]) >= 12 ? ' PM' : ' AM')}{isTimeDisabled && isToday && !isEditMode ? ' (Past)' : ''}
                          </option>
                        );
                      })}
                    </Select>
                  </div>

                  {/* Assign To */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-2">
                      Assign To
                    </label>
                    <Select
                      value={assignedTo}
                      onChange={(e) => {
                        const newAssignedTo = e.target.value;

                        // Early return only if value is empty — don't gate on leadId
                        // because in edit mode leadData may be null (leadId would be undefined)
                        if (!newAssignedTo) {
                          setAssignedTo(newAssignedTo);
                          return;
                        }

                        const leadId = enableLeadSelection ? selectedLead : (leadData?.id || editingFollowUp?.leadId);

                        // Determine the current owner of the lead
                        let currentOwnerId = "";
                        if (enableLeadSelection) {
                          const currentLead = availableLeads.find(l => l.value === leadId);
                          currentOwnerId = currentLead?.ownerId;
                        } else if (editingFollowUp) {
                          // In edit mode, compare against the originally assigned user
                          currentOwnerId =
                            editingFollowUp.AssignedToId ||
                            editingFollowUp.assignedToId ||
                            editingFollowUp.assignedTo ||
                            editingFollowUp.AssignedTo ||
                            leadData?.assignedTo;
                        } else {
                          currentOwnerId = leadData?.assignedTo;
                        }

                        // If user picks a different owner, show the reassignment warning
                        if (currentOwnerId && newAssignedTo !== String(currentOwnerId)) {
                          setPendingAssignedTo(newAssignedTo);
                          setShowReassignModal(true);
                        } else {
                          setAssignedTo(newAssignedTo);
                        }
                      }}
                      className="w-full"
                      disabled={loadingUsers || editingFollowUp?.isReschedule}
                    >
                      {loadingUsers ? (
                        <option value="">Loading users...</option>
                      ) : availableUsers.length > 0 ? (
                        <>
                          <option value="">Choose a user...</option>
                          {availableUsers.map(user => (
                            <option key={user.value} value={user.value}>
                              {user.label}
                            </option>
                          ))}
                        </>
                      ) : (
                        <option value="">No users available</option>
                      )}
                    </Select>
                    {loadingUsers && (
                      <div className="mt-1 text-xs text-gray-500 dark:text-dark-400 flex items-center gap-1">
                        <div className="animate-spin h-3 w-3 border border-gray-300 border-t-transparent rounded-full" />
                        Loading available users...
                      </div>
                    )}
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-2">
                      Notes
                    </label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add notes about this follow-up..."
                      rows={3}
                      className="w-full"
                    />
                  </div>

                  {/* Summary Card */}
                  <Card className={`p-4 ${editingFollowUp ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' : 'bg-gray-50 dark:bg-dark-800'}`}>
                    <h5 className="text-sm font-semibold text-gray-900 dark:text-dark-100 mb-3">
                      {editingFollowUp ? "Editing Follow-up Summary" : "Follow-up Summary"}
                    </h5>
                    {editingFollowUp && (
                      <div className="mb-3 p-2 bg-blue-100 dark:bg-blue-900/30 rounded text-xs text-blue-700 dark:text-blue-300">
                        📝 You are editing an existing follow-up
                      </div>
                    )}
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <CalendarDaysIcon className="h-4 w-4 text-gray-500 dark:text-dark-400" />
                        <span className="text-gray-700 dark:text-dark-200">{selectedDate ? formatDate(selectedDate) : "No date selected"}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <ClockIcon className="h-4 w-4 text-gray-500 dark:text-dark-400" />
                        <span className="text-gray-700 dark:text-dark-200">
                          {selectedTime && selectedTime.includes(':')
                            ? (parseInt(selectedTime.split(':')[0]) % 12 || 12) + ':' + selectedTime.split(':')[1] + (parseInt(selectedTime.split(':')[0]) >= 12 ? ' PM' : ' AM')
                            : 'No time selected'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {React.createElement(getTypeIcon(followUpType), { className: "h-4 w-4 text-gray-500 dark:text-dark-400" })}
                        <span className="text-gray-700 dark:text-dark-200">{followUpTypes.find(t => t.value === followUpType)?.label}</span>
                      </div>
                      {enableLeadSelection && (
                        <div className="flex items-center gap-2">
                          <UserIcon className="h-4 w-4 text-gray-500 dark:text-dark-400" />
                          <span className="text-gray-700 dark:text-dark-200">
                            <strong>Lead:</strong> {selectedLead ? availableLeads.find(l => l.value === selectedLead)?.name || "Loading..." : "Not selected"}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <UserIcon className="h-4 w-4 text-gray-500 dark:text-dark-400" />
                        <span className="text-gray-700 dark:text-dark-200">
                          <strong>Assigned:</strong> {assignedTo ? availableUsers.find(u => u.value === assignedTo)?.name || "Loading..." : "Not assigned"}
                        </span>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 dark:bg-dark-800 px-4 py-4 sm:px-6 flex items-center justify-end gap-3">
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                onClick={handleSubmit}
                disabled={isSubmitting || !selectedDate || !assignedTo || loadingUsers || (enableLeadSelection && (!selectedLead || loadingLeads))}
                className="flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    {editingFollowUp
                      ? (editingFollowUp.isReschedule ? "Rescheduling..." : "Updating...")
                      : "Scheduling..."
                    }
                  </>
                ) : (
                  <>
                    <CheckIcon className="h-4 w-4" />
                    {editingFollowUp
                      ? (editingFollowUp.isReschedule ? "Reschedule Follow-up" : "Update Follow-up")
                      : "Schedule Follow-up"
                    }
                  </>
                )}
              </Button>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>

      <ConfirmModal
        show={showReassignModal}
        onClose={() => {
          setShowReassignModal(false);
          setAssignedTo(pendingAssignedTo);
        }}
        onOk={async () => {
          const leadId = enableLeadSelection ? selectedLead : (leadData?.id || editingFollowUp?.leadId);
          setReassignLoading(true);
          try {
            const result = await updateLeadOwner(leadId, pendingAssignedTo);
            if (result.success) {
              setReassignState("success");
              setAssignedTo(pendingAssignedTo);
              // Update local leads data if available
              if (enableLeadSelection) {
                setAvailableLeads(prev => prev.map(l =>
                  l.value === leadId ? { ...l, ownerId: pendingAssignedTo } : l
                ));
              }

              // Close the modal after a short delay on success
              setTimeout(() => {
                setShowReassignModal(false);
                setReassignState("pending");
              }, 1500);
            } else {
              setReassignState("error");
              toast.error(result.error || "Failed to update lead owner");
            }
          } catch (error) {
            console.error("Error updating lead owner:", error);
            setReassignState("error");
            toast.error("An error occurred while updating lead owner");
          } finally {
            setReassignLoading(false);
          }
        }}
        confirmLoading={reassignLoading}
        state={reassignState}
        messages={{
          pending: {
            title: "Reassign Lead Owner?",
            description: "This owner is not assigned to this lead. Are you sure you want to reassign this owner to the lead?",
            actionText: "Yes, Reassign"
          },
          success: {
            title: "Lead Owner Reassigned",
            description: "The lead owner has been updated successfully."
          }
        }}
      />
    </Transition>
  );
};

export default FollowUpScheduleModal;
